import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { ServiceRequest, SurveyorProfile, RequestState } from '../../types';
import { apiClient } from '../../services/apiClient';
import { MapPicker } from '../../components/MapPicker';
import { toNumber } from '../../utils/number';

export const AdminRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<ServiceRequest | null>(null);
  const [surveyors, setSurveyors] = useState<SurveyorProfile[]>([]);
  const [selectedSurveyor, setSelectedSurveyor] = useState('');
  const [surveyorSearch, setSurveyorSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!id) return;
    Promise.all([
      apiClient.get(`/admin/requests/${id}/`).then(r => setRequest(r.data)),
      adminService.listSurveyors({ verification_status: 'APPROVED' }).then(r => setSurveyors(r.results ?? [])),
    ]).catch(() => {}).finally(() => setIsLoading(false));
  }, [id]);

  // Re-filter surveyors when request loads or search changes
  const filteredSurveyors = surveyors.filter((sv) => {
    if (!surveyorSearch) return true;
    const q = surveyorSearch.toLowerCase();
    return (
      sv.first_name.toLowerCase().includes(q) ||
      sv.last_name.toLowerCase().includes(q) ||
      sv.base_city.toLowerCase().includes(q) ||
      sv.service_types.some((t) => t.toLowerCase().includes(q))
    );
  });

  const perform = async (action: string, payload?: object) => {
    setActionLoading(true);
    try {
      const { data } = await apiClient.post(`/admin/requests/${id}/${action}/`, payload || {});
      setRequest(data);
      setMessage(`✅ Action "${action}" completed.`);
    } catch (e: any) {
      setMessage(`❌ ${e?.response?.data?.detail || e?.message || 'Action failed'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const rejectRequest = () => {
    if (!window.confirm('Reject this client booking? This will notify the client and remove any assigned surveyor.')) {
      return;
    }
    perform('reject', { reason: 'Client booking rejected by admin.' });
  };

  if (isLoading || !request) return <div style={s.center}><div style={s.spinner} /></div>;

  const isUnderReview = request.status === RequestState.UNDER_REVIEW || request.status === RequestState.REQUEST_SUBMITTED;
  const isApproved = request.status === RequestState.APPROVED;
  const canReject = ![RequestState.REJECTED, RequestState.CANCELLED, RequestState.CLOSED].includes(request.status);
  const canRestore = [RequestState.REJECTED, RequestState.CANCELLED].includes(request.status);

  return (
    <div>
      <button style={s.back} onClick={() => navigate(-1)}>← Back to Requests</button>
      <h1 style={s.title}>Request #{request.id.slice(0, 8)}</h1>

      {message && (
        <div style={{
          padding: '12px 16px', borderRadius: 10,
          background: message.startsWith('✅') ? 'var(--sc-success-bg)' : 'var(--sc-error-bg)',
          color: message.startsWith('✅') ? 'var(--sc-success-text)' : 'var(--sc-error-text)',
          marginBottom: 16, fontSize: 13, fontWeight: 600
        }}>
          {message}
        </div>
      )}

      <div style={s.grid}>
        <div>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Request Details</h3>
            {[
              ['Service Type', request.service_type], ['Status', request.status],
              ['Description', request.description], ['Address', request.address],
              ['Latitude', String(request.latitude)], ['Longitude', String(request.longitude)],
              ['Deadline', request.deadline || '—'], ['Preferred Date', request.preferred_date || '—'],
              ['Submitted', new Date(request.created_at).toLocaleString('en-ZW')],
            ].map(([l, v]) => (
              <div key={l} style={{ marginBottom: 10 }}>
                <div style={s.fieldLabel}>{l}</div>
                <div style={s.fieldVal}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ ...s.card, marginTop: 16 }}>
            <h3 style={s.cardTitle}>Client</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={s.avatar}>{request.client?.first_name?.[0] || '?'}{request.client?.last_name?.[0] || '?'}</div>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--sc-text-primary)' }}>{request.client.first_name} {request.client.last_name}</div>
                <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)' }}>{request.client.phone}</div>
              </div>
            </div>
          </div>

          {/* Map view */}
          <div style={{ ...s.card, marginTop: 16 }}>
            <h3 style={s.cardTitle}>Survey Location</h3>
            <MapPicker
              latitude={request.latitude}
              longitude={request.longitude}
              readOnly
              height={240}
            />
          </div>
        </div>

        <div>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Admin Actions</h3>

            {isUnderReview && (
              <div style={s.btnGroup}>
                <button style={s.approveBtn} disabled={actionLoading} onClick={() => perform('approve')}>
                  ✅ Approve Request
                </button>
              </div>
            )}

            {isApproved && (
              <div>
                <label style={s.fieldLabel}>Search &amp; Assign Surveyor</label>
                <input
                  type="text"
                  placeholder="Search by name, city, or service type…"
                  value={surveyorSearch}
                  onChange={(e) => setSurveyorSearch(e.target.value)}
                  style={{ ...s.select, marginBottom: 8 }}
                />
                <select style={s.select} value={selectedSurveyor} onChange={(e) => setSelectedSurveyor(e.target.value)}>
                  <option value="">Select surveyor…</option>
                  {filteredSurveyors.map((sv) => (
                    <option key={sv.id} value={sv.id}>
                      {sv.first_name} {sv.last_name} — {sv.base_city} · ⭐{toNumber(sv.rating_average).toFixed(1)} · {(sv.service_types ?? []).slice(0, 2).join(', ')}
                    </option>
                  ))}
                </select>
                <button
                  style={{ ...s.assignBtn, marginTop: 10, opacity: selectedSurveyor ? 1 : 0.5 }}
                  disabled={actionLoading || !selectedSurveyor}
                  onClick={() => perform('assign', { surveyor_id: selectedSurveyor })}
                >
                  👷 Assign & Notify
                </button>
              </div>
            )}

            {!isUnderReview && !isApproved && !request.assigned_surveyor && !canReject && !canRestore && (
              <p style={{ color: 'var(--sc-text-secondary)', fontSize: 13 }}>No actions available for status: <strong>{request.status}</strong></p>
            )}

            {canRestore && (
              <button
                style={{ ...s.approveBtn, marginTop: 10 }}
                disabled={actionLoading}
                onClick={() => perform('unreject')}
              >
                Restore for Review
              </button>
            )}

            {request.assigned_surveyor && (
              <button
                style={{ ...s.rejectBtn, marginTop: 10 }}
                disabled={actionLoading}
                onClick={() => perform('unassign', { reason: 'Surveyor unassigned by admin.' })}
              >
                Unassign Surveyor
              </button>
            )}

            {canReject && (
              <button
                style={{ ...s.rejectBtn, marginTop: 10 }}
                disabled={actionLoading}
                onClick={rejectRequest}
              >
                ❌ Reject Request
              </button>
            )}
          </div>

          {request.assigned_surveyor && (
            <div style={{ ...s.card, marginTop: 16 }}>
              <h3 style={s.cardTitle}>Assigned Surveyor</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={s.avatar}>{request.assigned_surveyor?.first_name?.[0] || '?'}{request.assigned_surveyor?.last_name?.[0] || '?'}</div>
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--sc-text-primary)' }}>{request.assigned_surveyor.first_name} {request.assigned_surveyor.last_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)' }}>{request.assigned_surveyor.base_city}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  back: { background: 'none', border: 'none', fontSize: 13, color: 'var(--sc-text-secondary)', cursor: 'pointer', padding: 0, marginBottom: 12, display: 'inline-block' },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 20px', fontFamily: "'Outfit', sans-serif" },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 },
  card: { background: 'var(--sc-bg-secondary)', borderRadius: 14, padding: 24, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px', fontFamily: "'Outfit', sans-serif" },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const, marginBottom: 3 },
  fieldVal: { fontSize: 14, color: 'var(--sc-text-primary)' },
  avatar: { width: 40, height: 40, borderRadius: 10, background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 },
  btnGroup: { display: 'flex', flexDirection: 'column', gap: 10 },
  approveBtn: { padding: '12px 20px', background: 'var(--sc-success)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' },
  rejectBtn: { padding: '12px 20px', background: 'var(--sc-error)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' },
  assignBtn: { padding: '12px 20px', background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' },
  select: { width: '100%', padding: '10px 14px', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 14, outline: 'none', background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)' },
};
